import _assign from "object-assign";

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { refType } from '@material-ui/utils';
import { detectOverflow } from '@popperjs/core';
import { useTheme } from '@jpmuitk/theme';

function within(min, value, max) {
  return Math.max(min, Math.min(value, max));
}

var CUSTOM_MODIFIER_NAME = 'keepVisible';
var keepVisible = {
  name: CUSTOM_MODIFIER_NAME,
  enabled: true,
  phase: 'main',
  requires: ['popperOffsets'],
  fn: function fn(_ref) {
    var state = _ref.state;
    var _state$rects = state.rects,
        referenceRect = _state$rects.reference,
        popperRect = _state$rects.popper,
        popperOffsets = state.modifiersData.popperOffsets; // Popper's absolute distance to the top of document/body

    var offset = popperOffsets.y; // Reference's Distance to the edge of the viewport (top of reference)

    var overflow = detectOverflow(state);
    var min = popperOffsets.y + overflow.top;
    var max = popperOffsets.y - overflow.bottom;
    var tetherMin = popperOffsets.y;
    var tetherMax = popperOffsets.y - popperRect.height + referenceRect.height;
    var preventedOffset = within(Math.min(min, tetherMin), offset, Math.max(max, tetherMax));
    popperOffsets.y = preventedOffset; // Move the popper left align with reference

    popperOffsets.x = referenceRect.x;
    var refVisibleTop = overflow.top - referenceRect.height < 0;
    var refVisibleBottom = overflow.bottom - popperRect.height < 0;
    state.modifiersData[CUSTOM_MODIFIER_NAME] = {
      refVisible: refVisibleTop && refVisibleBottom
    };
  }
};
export var usePopout = function usePopout(props) {
  var childRef = props.childRef,
      fullWidth = props.fullWidth,
      _props$showOnlyVisibl = props.showOnlyVisible,
      showOnlyVisible = _props$showOnlyVisibl === void 0 ? true : _props$showOnlyVisibl,
      preferredWidth = props.preferredWidth,
      preferredHeight = props.preferredHeight,
      onVisibleChange = props.onVisibleChange;

  var _useState = useState(null),
      placeholder = _useState[0],
      setPlaceholder = _useState[1];

  var _useState2 = useState(null),
      popper = _useState2[0],
      setPopper = _useState2[1];

  var _useState3 = useState(preferredHeight),
      placeholderHeight = _useState3[0],
      setPlaceholderHeight = _useState3[1];

  var _useState4 = useState(preferredWidth),
      placeholderWidth = _useState4[0],
      setPlaceholderWidth = _useState4[1];

  var _useState5 = useState(true),
      shouldShowPopper = _useState5[0],
      setShouldShowPopper = _useState5[1];

  var _useState6 = useState(false),
      sizeSet = _useState6[0],
      setSizeSet = _useState6[1];

  var _usePopper = usePopper(placeholder, popper, {
    placement: 'right-start',
    modifiers: [{
      name: 'flip',
      enabled: false
    }, {
      name: 'preventOverflow',
      enabled: false
    }, keepVisible]
  }),
      popperStyles = _usePopper.styles,
      popperState = _usePopper.state,
      update = _usePopper.update;

  useLayoutEffect(function () {
    if (!sizeSet && childRef.current) {
      setPlaceholderHeight(preferredHeight || childRef.current.getBoundingClientRect().height);
      setPlaceholderWidth(preferredWidth || childRef.current.getBoundingClientRect().width);
      setSizeSet(true);
    }
  }, [childRef, preferredHeight, preferredWidth, sizeSet]);

  var updateWidth = function updateWidth(newWidth) {
    if (newWidth) {
      // `Math.round` is in line with how CSS resolves sub-pixel values
      var width = Math.round(newWidth);
      setPlaceholderWidth(width);
    } // Caller can choose to not pass in a new width so we just remeasure
    else {
        setPlaceholderWidth(childRef.current.getBoundingClientRect().width);
      }
  };

  var updateHeight = function updateHeight(newHeight) {
    if (newHeight) {
      // `Math.round` is in line with how CSS resolves sub-pixel values
      var height = Math.round(newHeight);
      setPlaceholderHeight(height);
    } // Caller can choose to not pass in a new height so we will just remeasure
    else {
        setPlaceholderHeight(childRef.current.getBoundingClientRect().height);
      }
  };

  var popperTallerThanPlaceholder = sizeSet && popperState && popperState.rects ? popperState.rects.popper.height > placeholderHeight : false;
  var refVisible = popperState && popperState.modifiersData && popperState.modifiersData[CUSTOM_MODIFIER_NAME] ? popperState.modifiersData[CUSTOM_MODIFIER_NAME].refVisible : true;
  useEffect(function () {
    if (popperState) {
      var newVisible = showOnlyVisible ? refVisible : true;

      if (onVisibleChange) {
        if (shouldShowPopper && !newVisible) {
          onVisibleChange(false);
        } else if (!shouldShowPopper && newVisible) {
          onVisibleChange(true);
        }
      }

      setShouldShowPopper(newVisible);
    }
  }, [popperState, showOnlyVisible, refVisible, onVisibleChange, shouldShowPopper]);
  return {
    setPlaceholderRef: setPlaceholder,
    placeholderStyle: {
      width: fullWidth ? '100%' : placeholderWidth,
      height: placeholderHeight,
      overflowX: 'hidden',
      overflowY: 'hidden'
    },
    setPopperRef: setPopper,
    popperStyle: _assign({
      width: fullWidth ? '100%' : undefined,
      boxSizing: 'border-box'
    }, popperStyles.popper, {
      display: shouldShowPopper ? 'block' : 'none'
    }),
    popperTallerThanPlaceholder: popperTallerThanPlaceholder,
    actions: {
      // What's a better name for `refresh`, it's too generic?
      refresh: function refresh() {
        if (update) {
          update();
        }
      },
      updateWidth: updateWidth,
      updateHeight: updateHeight
    },
    // In theory this shouldn't be passed around, but the value in here can be used for other
    // popper placement calculation, e.g. dropdown menu for TokenizedInput
    popperState: popperState
  };
};
var Popout = /*#__PURE__*/forwardRef(function Popout(props, ref) {
  var children = props.children,
      childRef = props.childRef,
      fullWidth = props.fullWidth,
      showOnlyVisible = props.showOnlyVisible,
      preferredWidth = props.preferredWidth,
      preferredHeight = props.preferredHeight,
      onVisibleChange = props.onVisibleChange,
      restProps = _objectWithoutProperties(props, ["children", "childRef", "fullWidth", "showOnlyVisible", "preferredWidth", "preferredHeight", "onVisibleChange"]);

  var _usePopout = usePopout({
    childRef: childRef,
    fullWidth: fullWidth,
    showOnlyVisible: showOnlyVisible,
    preferredWidth: preferredWidth,
    preferredHeight: preferredHeight,
    onVisibleChange: onVisibleChange
  }),
      setPlaceholderRef = _usePopout.setPlaceholderRef,
      placeholderStyle = _usePopout.placeholderStyle,
      setPopperRef = _usePopout.setPopperRef,
      popperStyle = _usePopout.popperStyle,
      actions = _usePopout.actions,
      popperTallerThanPlaceholder = _usePopout.popperTallerThanPlaceholder;

  useImperativeHandle(ref, function () {
    return {
      refresh: actions.refresh,
      updateWidth: actions.updateWidth,
      updateHeight: actions.updateHeight
    };
  }, [actions]);

  var _useTheme = useTheme(),
      toolkit = _useTheme.toolkit;

  var getElevation = toolkit.elevation.getElevation,
      _toolkit$palette = toolkit.palette,
      type = _toolkit$palette.type,
      blue300 = _toolkit$palette.blue300,
      blue500 = _toolkit$palette.blue500; // This is more or less a demo component, so not adding complexity of using `withStyles`

  var elevatedStyle = {
    // We want a border with boxShadow, but 'borderWidth' property ruins height calculation
    // So we're using a boxShadow to simulate the border. We're also not using 'inset' as FormField
    // has a solid background which will block it.
    boxShadow: "".concat(getElevation(4, 'blue').boxShadow[0].reduce(function (prev, current) {
      return "".concat(prev, " ").concat(Number.isInteger(current) ? "".concat(current, "px") : current);
    }, ''), ", 0 0 0 1px ").concat(type === 'light' ? blue500 : blue300)
  };
  return /*#__PURE__*/React.createElement("div", _assign({
    ref: ref
  }, restProps), /*#__PURE__*/React.createElement("div", {
    ref: setPlaceholderRef,
    style: placeholderStyle
  }), /*#__PURE__*/React.createElement("div", {
    ref: setPopperRef,
    style: _assign({}, popperStyle, popperTallerThanPlaceholder ? elevatedStyle : {})
  }, children));
});
process.env.NODE_ENV !== "production" ? Popout.propTypes = {
  /**
   * Pass a ref callback to child node for calculating size.
   */
  childRef: refType,

  /**
   * The content of the component.
   */
  children: PropTypes.node,

  /**
   * Whether the popout container should be in full width. Useful when used in FormField.
   */
  fullWidth: PropTypes.bool,

  /**
   * Callback when pop out visibility changes
   */
  onVisibleChange: PropTypes.func,

  /**
   * Preferred height of the space should be occupied. If ignored, will try to use the height
   * of the child component on mount.
   */
  preferredHeight: PropTypes.number,

  /**
   * Preferred width of the space should be occupied. If ignored, will try to use the width
   * of the child component on mount.
   */
  preferredWidth: PropTypes.number,

  /**
   * Shows the pop out only when it's children is visible.
   * When it is set to `false`, pop out will stay visible while tethering to the boundary to the children.
   *
   * @default true
   */
  showOnlyVisible: PropTypes.bool
} : void 0;
export default Popout;